import { addDays, format, setHours, setMinutes } from 'date-fns';

export interface ShiftFrame {
  id: string;
  label: string;
  start: string;
  end: string;
  isOvernight: boolean;
}

export interface ShiftBounds {
  start: Date;
  end: Date;
}

export const DEFAULT_SHIFT_FRAMES: ShiftFrame[] = [
  { id: 'overnight', label: 'Overnight', start: '00:00', end: '08:00', isOvernight: false },
  { id: 'morning', label: 'Morning', start: '08:00', end: '16:00', isOvernight: false },
  { id: 'afternoon', label: 'Afternoon', start: '16:00', end: '00:00', isOvernight: true },
];

export const DAILY_REPORT_FRAME: ShiftFrame = {
  id: 'daily-report',
  label: 'Daily Report',
  start: '00:00',
  end: '23:59',
  isOvernight: false,
};

export const isValidHHMM = (value: string): boolean => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return false;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

export const normalizeHHMM = (value: string): string | null => {
  const trimmed = value.trim();
  if (!isValidHHMM(trimmed)) return null;
  const [hours, minutes] = trimmed.split(':').map(Number);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const compareHHMM = (a: string, b: string): number => a.localeCompare(b);

export const isOvernightShift = (start: string, end: string): boolean => {
  const normalizedStart = normalizeHHMM(start);
  const normalizedEnd = normalizeHHMM(end);
  if (!normalizedStart || !normalizedEnd) return false;
  return normalizedEnd <= normalizedStart;
};

const slugifyShiftId = (label: string, index: number): string => {
  const slug = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || `shift-${index + 1}`;
};

export const normalizeShiftConfig = (raw: unknown): ShiftFrame[] => {
  const rows = Array.isArray(raw) ? raw : [];
  const normalized = rows
    .map((row, index) => {
      const cfg = row && typeof row === 'object' ? row as Record<string, unknown> : {};
      const label = typeof cfg.label === 'string' ? cfg.label.trim() : '';
      const start = typeof cfg.start === 'string' ? normalizeHHMM(cfg.start) : null;
      const end = typeof cfg.end === 'string' ? normalizeHHMM(cfg.end) : null;
      if (!label || !start || !end) return null;
      return {
        id: typeof cfg.id === 'string' && cfg.id.trim() ? cfg.id.trim() : slugifyShiftId(label, index),
        label,
        start,
        end,
        isOvernight: typeof cfg.isOvernight === 'boolean' ? cfg.isOvernight : isOvernightShift(start, end),
      } satisfies ShiftFrame;
    })
    .filter((row): row is ShiftFrame => Boolean(row));

  return normalized.length > 0 ? normalized : DEFAULT_SHIFT_FRAMES;
};

export const getShiftBounds = (date: Date, shift: ShiftFrame): ShiftBounds => {
  const base = new Date(date);
  base.setHours(0, 0, 0, 0);
  const [startHour, startMinute] = shift.start.split(':').map(Number);
  const [endHour, endMinute] = shift.end.split(':').map(Number);
  const start = setMinutes(setHours(base, startHour), startMinute);
  const rawEnd = setMinutes(setHours(base, endHour), endMinute);
  const end = shift.isOvernight || rawEnd <= start ? addDays(rawEnd, 1) : rawEnd;
  return { start, end };
};

export const getCurrentShiftSelection = (shifts: ShiftFrame[], now = new Date()): { date: Date; shift: ShiftFrame } => {
  const availableShifts = shifts.length > 0 ? shifts : DEFAULT_SHIFT_FRAMES;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const yesterday = addDays(today, -1);

  for (const date of [today, yesterday]) {
    const current = availableShifts.find((shift) => {
      const bounds = getShiftBounds(date, shift);
      return now >= bounds.start && now < bounds.end;
    });
    if (current) return { date, shift: current };
  }

  return { date: today, shift: availableShifts[0] };
};

export const formatShiftLabel = (shift: ShiftFrame): string => (
  `${shift.label} (${format(new Date(`2000-01-01T${shift.start}:00`), 'h:mm a')} – ${format(new Date(`2000-01-01T${shift.end}:00`), 'h:mm a')})`
);

export const resolveShiftLabel = (
  shifts: ShiftFrame[],
  at: Date | string | null | undefined,
  fallback?: string | null,
): string => {
  const trimmed = (fallback ?? '').toString().trim();
  if (trimmed && trimmed.toLowerCase() !== 'current') return trimmed;
  if (!at) return trimmed || 'Shift';
  const date = typeof at === 'string' ? new Date(at) : at;
  if (isNaN(date.getTime())) return trimmed || 'Shift';
  const available = shifts.length > 0 ? shifts : DEFAULT_SHIFT_FRAMES;
  for (const candidate of [date, addDays(date, -1)]) {
    const match = available.find((shift) => {
      const bounds = getShiftBounds(candidate, shift);
      return date >= bounds.start && date < bounds.end;
    });
    if (match) return match.label;
  }
  return trimmed || 'Shift';
};

export const validateShiftFrames = (shifts: ShiftFrame[]): string | null => {
  if (shifts.length === 0) return 'At least one shift time frame is required.';
  const labels = new Set<string>();
  for (const shift of shifts) {
    if (!shift.label.trim()) return 'Every shift needs a name.';
    if (!normalizeHHMM(shift.start) || !normalizeHHMM(shift.end)) return 'Every shift needs valid HH:MM start and end times.';
    const key = shift.label.trim().toLowerCase();
    if (labels.has(key)) return 'Shift names must be unique.';
    labels.add(key);
  }
  return null;
};